import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Mission', href: '#mission' },
  { label: 'Services', href: '#services' },
  { label: 'Certificate', href: '#certificate' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-axis-900/5 border-b border-axis-100/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto section-padding">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <a href="#" className="flex items-center gap-4 group">
            <img
              src="/logo.png"
              alt="AXIS Cleaning Services Logo"
              className="w-24 h-24 rounded-2xl object-contain shadow-lg shadow-axis-500/30 group-hover:shadow-axis-500/50 transition-shadow duration-300"
            />
            <div className="flex flex-col">
              <span className={`font-display text-3xl font-semibold tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-axis-900' : 'text-white'
              }`}>
                AXIS
              </span>
              <span className={`text-xs uppercase tracking-[0.3em] font-medium transition-colors duration-300 ${
                scrolled ? 'text-axis-500' : 'text-axis-200'
              }`}>
                Cleaning Services
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-axis-500/10 ${
                  scrolled ? 'text-axis-800 hover:text-axis-600' : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA + Phone */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:2407509535"
              className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 ${
                scrolled ? 'text-axis-700' : 'text-white/90'
              }`}
            >
              <Phone className="w-4 h-4" />
              240-750-9535
            </a>
            <a
              href="#contact"
              className="clean-gradient text-white px-6 py-2.5 rounded-full text-sm font-semibold shadow-lg shadow-axis-500/30 hover:shadow-axis-500/50 hover:scale-105 transition-all duration-300"
            >
              Get a Quote Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-axis-800 hover:bg-axis-50' : 'text-white hover:bg-white/10'
            }`}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-axis-100/50"
          >
            <div className="section-padding py-6 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 text-axis-800 rounded-xl text-sm font-medium hover:bg-axis-50 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-4 border-t border-axis-100 mt-4 space-y-3">
                <a
                  href="tel:2407509535"
                  className="flex items-center gap-2 px-4 py-2 text-axis-700"
                >
                  <Phone className="w-4 h-4" />
                  240-750-9535
                </a>
                <a
                  href="#contact"
                  onClick={() => setIsOpen(false)}
                  className="block clean-gradient text-white text-center px-6 py-3 rounded-full font-semibold"
                >
                  Get a Quote Now
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
